import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { AuthenticationService } from '../../services/authentication.service';
import { ModalService } from '../../services/modal.service';
import { CreditPack, isSmallPack } from '../../classes/app.models';

/** One priced combination: a pack size at a licence tier. */
export interface PackOption {
    /** The credit-pack row /shop/checkout/:id buys. */
    packId: number | null;
    /** Rupees. */
    price: number | null;
    /** The line beside the price, e.g. "one-time · 25 image pack" (sub_title). */
    note: string;
    /** The ticked lines (description, one per line). */
    includes: string[];
}

export interface PackSize {
    key: string;
    label: string;
    options: { [tier: string]: PackOption };
}

/** The copy half of a tab, as one-time-packs.json supplies it. */
export interface PackTabCopy {
    key: string;
    label: string;
    /** The pill beside the tab name, e.g. "Coming soon". Null for none. */
    badge: string | null;
    /** False puts the panel in its coming-soon state and disables its button. */
    available: boolean;
    title: string;
    description: string;
    features: string[];
    cta: string;
    footnote: string;
    caption: string;
    tagline: string;
    tiers: { key: string; label: string }[];
}

/** A tab once its sizes have been filled in from the API. */
export interface PackTab extends PackTabCopy {
    sizes: PackSize[];
}

export interface OneTimePacksCopy {
    heading: string;
    lede: string;
    tabs: PackTabCopy[];
}

export interface OneTimePacks {
    heading: string;
    lede: string;
    tabs: PackTab[];
}

/**
 * The one-time packs section, shared by /subscription and the pricing dialog.
 *
 * One component rather than the same markup in both templates: it is a hundred
 * lines of layout over three pieces of selection state, and this codebase has
 * already been bitten by the same figures living in two files and drifting
 * apart. Both hosts import SharedModule, so neither needs anything of its own.
 *
 * TWO SOURCES, AND THE SPLIT IS DELIBERATE.
 *
 *   assets/data/one-time-packs.json   tab-level COPY only — headings, the
 *                                     feature list, the CTA label, the footnote,
 *                                     and whether a tab is on sale at all.
 *   auth/plan                         the PACKS: which sizes exist, what they
 *                                     cost, what each card lists, and the id the
 *                                     Buy button checks out.
 *
 * The prices used to live in the JSON too, with a packId to be pasted in by
 * hand. They do not any more: a price in a static asset is a price that can
 * disagree with what the customer is actually charged, and this section was
 * shipping with every Buy button dead because nobody had pasted the ids.
 *
 * WHAT MAKES A PACK APPEAR HERE is pack = SMALL_PACK. The same endpoint feeds
 * the subscription rail, which takes LARGE_PACK; see isSmallPack().
 */
@Component({
    selector: 'one-time-packs',
    templateUrl: './one-time-packs.component.html',
    styleUrls: ['./one-time-packs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OneTimePacksComponent implements OnInit {

    /**
     * 'full' is the pricing-page section. 'compact' is the card beside an image
     * on the media detail page: image packs only, no headings or tabs, opening
     * on the smallest pack rather than the largest.
     */
    @Input() variant: 'full' | 'compact' = 'full';

    data: OneTimePacks | null = null;

    activeTab = '';
    activeTier = '';
    activeSize = '';

    constructor(private http: HttpClient,
                private apiService: ApiService,
                private router: Router,
                private authService: AuthenticationService,
                private modalService: ModalService,
                private cdref: ChangeDetectorRef) {
    }

    get compact(): boolean {
        return this.variant === 'compact';
    }

    ngOnInit(): void {
        /*
         * skipLoader on the JSON: this is a page section, not an action the
         * reader is waiting on, and the global overlay has no business covering
         * the page it decorates. See AppInterceptor.
         *
         * Both calls swallow their own failures so one cannot take the other
         * down — but the section still needs both to say anything useful, and
         * build() returns null if either came back empty. A pricing page is
         * better missing one block than showing an empty frame where prices
         * should be.
         */
        forkJoin([
            this.http.get<OneTimePacksCopy>('assets/data/one-time-packs.json', { params: { skipLoader: 'true' } })
                .pipe(catchError(() => of(null))),
            this.apiService.getPlanDetail().pipe(catchError(() => of(null)))
        ]).subscribe(([copy, response]: [OneTimePacksCopy, any]) => {
            const packs: CreditPack[] = Array.isArray(response) ? response : ((response && response.data) || []);

            this.data = this.build(copy, packs);
            this.selectTab(this.data ? this.data.tabs[0].key : '');
            this.cdref.markForCheck();
        });
    }

    /**
     * Merge the copy with the packs.
     *
     * Returns null — which renders nothing — unless at least one tab ended up
     * with something to sell. A tab with no sizes would draw its heading, its
     * feature list and an empty price panel, which reads as broken rather than
     * as "not available".
     */
    private build(copy: OneTimePacksCopy, packs: CreditPack[]): OneTimePacks | null {
        if (!copy || !copy.tabs || !copy.tabs.length) {
            return null;
        }

        /*
         * status/ip_pack_status are filtered server side; repeated here so a pack
         * switched off can never reach a card through a cached or proxied
         * response. isSmallPack is what keeps the subscription packs out.
         */
        const live = (packs || []).filter(p =>
            Number(p.status) === 1 && Number(p.ip_pack_status) === 1 && isSmallPack(p));

        const tabs = copy.tabs
            // Beside an image, only image packs are relevant.
            .filter(tab => !this.compact || tab.key === 'image')
            .map(tab => ({
                ...tab,
                // The tab key IS the media value, lower-cased: image | video.
                sizes: this.sizesFor(live.filter(p => this.mediaOf(p) === (tab.key || '').toUpperCase()), tab)
            }));

        return tabs.some(t => t.sizes.length)
            ? { heading: copy.heading, lede: copy.lede, tabs }
            : null;
    }

    /**
     * Group one tab's packs into size buttons.
     *
     * The label is the join: "25 Images" is one button, and the STANDARD and
     * ENHANCED rows sharing that label are its two tiers. Which is why
     * labelOf() must be deterministic — two spellings of the same size split the
     * strip into two buttons that each offer only one tier.
     */
    private sizesFor(packs: CreditPack[], tab: PackTabCopy): PackSize[] {
        const tierKeys = (tab.tiers || []).map(t => (t.key || '').toUpperCase());
        const byTitle: { [title: string]: PackSize } = {};
        const order: string[] = [];

        packs.forEach(pack => {
            const title = this.labelOf(pack);
            const tier = (pack.type || '').trim().toUpperCase();

            // A tier the tab does not offer has no button to sit under.
            if (!title || !tier || tierKeys.indexOf(tier) === -1) {
                return;
            }

            if (!byTitle[title]) {
                byTitle[title] = { key: title, label: title, options: {} };
                order.push(title);
            }

            byTitle[title].options[tier.toLowerCase()] = {
                packId: pack.id,
                price: this.priceOf(pack),
                // Optional, like title: a blank sub_title costs the line beside
                // the price, not the card.
                note: (pack.sub_title || '').trim(),
                includes: this.linesOf(pack.description)
            };
        });

        /*
         * Cheapest first, which is also smallest first — there is no size column
         * to sort on, and price is the one ordering that cannot disagree with
         * what the buttons show. Sorted on the cheapest tier of each size so the
         * strip does not reorder when the reader switches tier.
         */
        return order
            .map(title => byTitle[title])
            .sort((a, b) => this.cheapest(a) - this.cheapest(b));
    }

    private cheapest(size: PackSize): number {
        const prices = Object.keys(size.options).map(k => size.options[k].price || 0);
        return prices.length ? Math.min.apply(Math, prices) : 0;
    }

    /**
     * What the pack costs: its credits at its own per-credit rate, the same
     * product every backend caller computes.
     *
     * ROUNDED TO THE RUPEE for display. The stored rate is only two decimals, so
     * several packs multiply out a few paise off their advertised figure —
     * 11 × 1363.64 is 15,000.04, not 15,000. Checkout prices the pack itself, so
     * this rounding changes the card, never the charge.
     */
    private priceOf(pack: CreditPack): number {
        return Math.round(Number(pack.total_credits || 0) * Number(pack.credit_value_inr || 0));
    }

    /**
     * The size-button label.
     *
     * `title` when the pack has one. It is NOT required, because it is an
     * optional field on an admin form and the section must not vanish because
     * someone left it blank — which is exactly what happened: fourteen priced
     * packs with NULL titles produced no size buttons, no sizes meant no tab had
     * anything to sell, and the whole section rendered nothing.
     *
     * The fallback reads the size out of pack_name by dropping a trailing
     * "(Standard)" / "(Enhanced)" and a leading "One-Time", so
     * "One-Time 25 Images (Enhanced)" and "One-Time 25 Images (Standard)" both
     * come back "25 Images" and pair up under one button. A name it cannot parse
     * is used whole rather than discarded.
     */
    private labelOf(pack: CreditPack): string {
        const title = (pack.title || '').trim();
        if (title) {
            return title;
        }

        const name = (pack.pack_name || '').trim();
        return name.replace(/\s*\([^)]*\)\s*$/, '').replace(/^one[-\s]?time\s+/i, '').trim() || name;
    }

    /**
     * The ticked lines, one per entry.
     *
     * `description` is edited in the admin, which writes it as HTML list items
     * ("<li>25 images included</li>") separated by CRLF. The template draws its
     * own <li> and tick icon around each entry, so the markup is stripped rather
     * than rendered: putting it through [innerHTML] would nest a list inside a
     * list item AND make a database column an injection surface.
     *
     * Split on the closing tag as well as on newlines, so it reads the same
     * whether the admin writes one item per line or runs them together — and
     * plain text, with no tags at all, still works.
     */
    private linesOf(description: string): string[] {
        return (description || '')
            .split(/<\/li>|<br\s*\/?>|\r?\n/i)
            .map(l => l.replace(/<[^>]*>/g, '')
                       .replace(/&nbsp;/gi, ' ')
                       .replace(/&amp;/gi, '&')
                       .trim())
            .filter(l => !!l);
    }

    /** IMAGE | VIDEO, falling back to the older pack_type where media is unset. */
    private mediaOf(pack: CreditPack): string {
        return ((pack.media || pack.pack_type || '') as string).trim().toUpperCase();
    }

    get tab(): PackTab | null {
        if (!this.data) { return null; }
        return this.data.tabs.filter(t => t.key === this.activeTab)[0] || null;
    }

    /**
     * The selected size, falling back to the first.
     *
     * Never null while a tab with sizes is loaded, so the template does not need
     * a second empty state for "a tab with sizes, none of them chosen".
     */
    get size(): PackSize | null {
        const tab = this.tab;
        if (!tab || !tab.sizes.length) { return null; }
        return tab.sizes.filter(s => s.key === this.activeSize)[0] || tab.sizes[0];
    }

    /** The priced row behind the current tab + size + tier. */
    get option(): PackOption | null {
        const size = this.size;
        if (!size) { return null; }
        return size.options[this.activeTier] || null;
    }

    /**
     * Switching tabs resets the tier and size to that tab's own first entries.
     *
     * Carrying them across would ask for "25 Images" on the video tab, which has
     * no such size, and leave the panel blank.
     */
    selectTab(key: string): void {
        this.activeTab = key;

        const tab = this.tab;
        if (!tab) {
            this.activeTier = '';
            this.activeSize = '';
            return;
        }

        this.activeTier = tab.tiers.length ? tab.tiers[0].key : '';
        // The pricing page opens on the largest pack, as its design does; the
        // compact card beside one image opens on the smallest.
        this.activeSize = !tab.sizes.length ? ''
            : (this.compact ? tab.sizes[0].key : tab.sizes[tab.sizes.length - 1].key);
    }

    selectTier(key: string): void {
        this.activeTier = key;
    }

    /** "Standard" / "Enhanced", for the licence link beside the price. */
    get tierLabel(): string {
        const tab = this.tab;
        const tier = tab ? tab.tiers.filter(t => t.key === this.activeTier)[0] : null;
        return tier ? tier.label : '';
    }

    selectSize(key: string): void {
        this.activeSize = key;
    }

    /**
     * Whether this combination can actually be bought.
     *
     * Three things have to hold, and each one is a different kind of "not yet":
     * the tab has to be on sale at all (video is not), there has to be a priced
     * row behind the current selection, and it has to name a pack to check out.
     */
    get canBuy(): boolean {
        const tab = this.tab;
        const option = this.option;

        return !!tab && tab.available
            && !!option && !!option.price && !!option.packId;
    }

    /** ₹ with Indian digit grouping — "2,00,000", not "200,000". */
    inr(value: number | null): string {
        return '₹' + (Number(value) || 0).toLocaleString('en-IN');
    }

    /** The same checkout the subscription cards use. */
    buy(): void {
        const option = this.option;
        if (!this.canBuy || !option) { return; }

        /*
         * The compact card is shown to signed-out visitors, and /shop sits behind
         * AuthGuard, which would bounce them to the homepage. Ask them to sign in
         * here instead.
         */
        if (this.compact && !this.authService.currentUserValue) {
            this.modalService.showLoginModal();
            return;
        }

        this.router.navigate(['/shop/checkout', option.packId]);
    }

    /*
     * Generic, not `item: { key: string }`.
     *
     * Under strictTemplates the trackBy signature narrows the *ngFor item type,
     * so a concrete parameter here told the checker every row was a bare
     * { key } and every other property on it vanished.
     */
    trackByKey<T extends { key: string }>(_: number, item: T): string {
        return item.key;
    }
}
